import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { useLanguage } from 'hooks/useLanguage';

import LoginForm from './components/LoginForm';
import SocialLogin from './components/SocialLogin';
import TwoFactorModal from './components/TwoFactorModal';

const LoginScreen = () => {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTwoFA, setShowTwoFA] = useState(false);
  const [tempAuthData, setTempAuthData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
  };

  const validateForm = () => {
    if (!formData?.email?.trim()) {
      setError('Please enter your email');
      return false;
    }
    if (!formData?.password?.trim()) {
      setError('Please enter your password');
      return false;
    }
    return true;
  };

  const handleLogin = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${import.meta.env?.VITE_ZONEX_API_URL || 'https://manager.zonextrader.com/api'}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData?.email,
          password: formData?.password
        })
      });

      const data = await response?.json();

      if (!response?.ok) {
        throw new Error(data.message || 'Invalid credentials');
      }

      // Check if 2FA is required
      if (data?.requires_2fa) {
        setTempAuthData({
          token: data?.temp_token,
          user_id: data?.user_id
        });
        setShowTwoFA(true);
        return;
      }

      // Store authentication data
      localStorage.setItem('zonex_auth_token', data?.token);
      localStorage.setItem('zonex_user', JSON.stringify(data?.user));

      // Initialize WebSocket connection
      initializeWebSocket(data?.token);

      // Redirect to dashboard
      navigate('/main-trading-dashboard');

    } catch (error) {
      console.error('Login error:', error);
      setError(error?.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const initializeWebSocket = (token) => {
    try {
      const wsUrl = import.meta.env?.VITE_ZONEX_WS_URL || 'wss://general.zonextrader.com/';
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('WebSocket connected to Zonex');
        ws?.send(JSON.stringify({
          action: 'authenticate',
          token: token
        }));
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event?.data);
        console.log('WebSocket message:', data);
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      // Store WebSocket instance globally for later use
      window.zonexWebSocket = ws;
      
    } catch (error) {
      console.error('WebSocket initialization failed:', error);
    }
  };

  const handleTwoFactorSubmit = async (twoFACode) => {
    setIsLoading(true);
    
    try {
      const response = await fetch(`${import.meta.env?.VITE_ZONEX_API_URL || 'https://manager.zonextrader.com/api'}/auth/verify-2fa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          temp_token: tempAuthData?.token,
          user_id: tempAuthData?.user_id,
          code: twoFACode
        })
      });

      const data = await response?.json();

      if (!response?.ok) {
        throw new Error(data.message || 'Invalid 2FA code');
      }

      // Store authentication data
      localStorage.setItem('zonex_auth_token', data?.token);
      localStorage.setItem('zonex_user', JSON.stringify(data?.user));

      // Initialize WebSocket connection
      initializeWebSocket(data?.token);

      setShowTwoFA(false);
      navigate('/main-trading-dashboard');

    } catch (error) {
      console.error('2FA verification error:', error);
      setError(error?.message || '2FA verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLogin = (provider) => {
    console.log(`Social login with ${provider}`);
    // Implement social login logic
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - FINOTIC Dashboard Preview */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-50 to-indigo-100 relative overflow-hidden">
        <div className="flex flex-col justify-center items-center w-full p-12 relative z-10">
          {/* FINOTIC Logo and Brand */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-800 mb-2">FINOTIC</h1>
          </div>

          {/* Dashboard Preview Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
            {/* Current Balance */}
            <div className="mb-8">
              <p className="text-gray-600 text-sm mb-1">CURRENT BALANCE</p>
              <h2 className="text-3xl font-bold text-gray-800">$24,359</h2>
            </div>

            {/* Pie Chart Section */}
            <div className="mb-8 flex items-center justify-center">
              <div className="relative w-32 h-32">
                {/* Simple Pie Chart using CSS */}
                <div className="w-32 h-32 rounded-full bg-gradient-to-r from-orange-400 via-pink-400 to-purple-400 relative">
                  <div className="absolute inset-2 bg-white rounded-full flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-800">34%</div>
                      <div className="text-xs text-gray-600">Food</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Add Transaction Button */}
            <div className="text-center mb-6">
              <button className="w-12 h-12 bg-blue-500 hover:bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3 transition-colors">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
              <p className="text-sm text-gray-600">New transaction</p>
              <p className="text-sm text-gray-600">or upload .xls file</p>
            </div>
          </div>

          {/* Bottom Welcome Section */}
          <div className="mt-8 text-center">
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Welcome back!</h3>
            <p className="text-gray-600">Start managing your finance faster and better</p>
          </div>
        </div>

        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 400 400" fill="none">
            <circle cx="100" cy="100" r="60" fill="currentColor" className="text-blue-300" />
            <circle cx="300" cy="150" r="40" fill="currentColor" className="text-indigo-300" />
            <circle cx="200" cy="300" r="80" fill="currentColor" className="text-purple-300" />
          </svg>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Welcome Section */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-2">Welcome back!</h2>
            <p className="text-gray-600">Start managing your finance faster and better</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <span className="text-red-700 text-sm">{error}</span>
            </div>
          )}

          {/* Login Form */}
          <LoginForm
            formData={formData}
            onInputChange={handleInputChange}
            showPassword={showPassword}
            onTogglePassword={() => setShowPassword(!showPassword)}
            onSubmit={handleLogin}
            isLoading={isLoading}
            onForgotPassword={handleForgotPassword}
          />

          {/* Social Login Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">or</span>
            </div>
          </div>

          {/* Social Login */}
          <SocialLogin onSocialLogin={handleSocialLogin} />

          {/* Register Link */}
          <div className="text-center mt-8">
            <p className="text-gray-600 text-sm">
              Don't you have an account?{' '}
              <Link 
                to="/register-screen" 
                className="text-blue-600 hover:text-blue-700 font-medium hover:underline transition-colors"
              >
                Sign Up
              </Link>
            </p>
          </div>

          {/* Copyright */}
          <div className="text-center mt-6">
            <p className="text-xs text-gray-400">© 2022 ALL RIGHTS RESERVED</p>
          </div>
        </div>
      </div>

      {/* Two Factor Authentication Modal */}
      {showTwoFA && (
        <TwoFactorModal
          isOpen={showTwoFA}
          onClose={() => setShowTwoFA(false)}
          onSubmit={handleTwoFactorSubmit}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

export default LoginScreen;